import React from 'react';
import { Box, Link, Typography } from '@mui/material';

import SlideSidePane from '../../Views/Shared/SlideSidePane';

import FormPreview from './FormPreview';

interface FormPreviewSlidePaneProps {
  open: boolean;
  onClose: () => void;
  formId: string | null;
  title?: string;
  draft?: boolean;
  showCustomizeLink?: boolean;
  customizeLinkText?: string;
  subjectLanguageCap?: string;
  isCustomizing?: boolean;
  onCustomize?: () => void;
}

/**
 * Reusable component that combines SlideSidePane with FormPreview
 * Used across form-library, form-builder, and patient forms
 * @param draft - If true, fetches the draft version of the form (used in form-builder)
 * @param showCustomizeLink - If true, shows a customize link at the bottom
 * @param customizeLinkText - Custom text for the customize link
 * @param subjectLanguageCap - Capitalized subject language for customize text
 * @param isCustomizing - If true, shows "Customizing..." instead of the link
 * @param onCustomize - Callback when customize link is clicked
 */
export const FormPreviewSlidePane: React.FC<FormPreviewSlidePaneProps> = ({
  open,
  onClose,
  formId,
  title = 'Form Preview',
  draft = false,
  showCustomizeLink = false,
  customizeLinkText,
  subjectLanguageCap = 'Patient',
  isCustomizing = false,
  onCustomize,
}) => {
  return (
    <SlideSidePane open={open} onClose={onClose} title={title}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          p: 2,
          overflow: 'auto',
        }}
      >
        {formId && (
          <>
            <FormPreview formId={formId} draft={draft} />

            {showCustomizeLink && (
              <Box sx={{ mt: 4, mb: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {customizeLinkText || 'Not quite right?'}{' '}
                  {isCustomizing ? (
                    <span style={{ color: 'text.disabled' }}>Customizing...</span>
                  ) : (
                    <Link
                      component="button"
                      variant="body2"
                      onClick={onCustomize}
                      underline="always"
                      sx={{
                        cursor: 'pointer',
                        border: 'none',
                        background: 'none',
                        padding: 0,
                        font: 'inherit',
                        color: 'primary.main',
                      }}
                    >
                      Customize this {subjectLanguageCap} Form
                    </Link>
                  )}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </SlideSidePane>
  );
};

export default FormPreviewSlidePane;
